import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Trophy, Heart, DollarSign, Clock, Crown, Film, Radio, Award,
  Loader2, ArrowLeft, Sparkles,
} from "lucide-react";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const SORTS = [
  { key: "followers", label: "Most followers", Icon: Heart },
  { key: "tickets", label: "Top revenue · 7d", Icon: DollarSign },
  { key: "watch_time", label: "Most watched", Icon: Clock },
];

function fmt(n) {
  if (n === undefined || n === null) return "—";
  if (typeof n !== "number") return String(n);
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function money(n) {
  if (!n) return "$0";
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function RoleBadge({ creator }) {
  if (creator.is_admin) return <span className="text-[10px] uppercase tracking-widest font-bold text-[#E50914]">Admin</span>;
  if (creator.is_filmmaker) return <span className="text-[10px] uppercase tracking-widest font-bold text-amber-400">Filmmaker</span>;
  if (creator.is_channel_owner) return <span className="text-[10px] uppercase tracking-widest font-bold text-cyan-300">Channel Owner</span>;
  if (creator.is_content_owner) return <span className="text-[10px] uppercase tracking-widest font-bold text-violet-300">Content Owner</span>;
  return null;
}

function CreatorCard({ c, rank, sortKey }) {
  const initials = (c.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const rankBg =
    rank === 1 ? "from-[#f6c34a] to-[#b8770c] text-black" :
    rank === 2 ? "from-gray-300 to-gray-500 text-black" :
    rank === 3 ? "from-amber-700 to-amber-900 text-white" :
                 "from-white/5 to-white/0 text-gray-500 border border-white/10";
  const headlineStat =
    sortKey === "tickets" ? { value: money(c.ticket_revenue_7d), label: "Revenue · 7d" } :
    sortKey === "watch_time" ? { value: `${fmt(c.watch_minutes)}m`, label: "Watched" } :
    { value: fmt(c.followers), label: "Followers" };
  return (
    <Link
      to={`/creator/${c.user_id}`}
      data-testid={`leaderboard-card-${c.user_id}`}
      className="block rounded-xl border border-white/10 bg-[#0e0e0e] hover:border-[#f6c34a]/40 hover:-translate-y-0.5 transition p-5"
    >
      <div className="flex items-start gap-4 mb-4">
        {/* Rank badge */}
        <div className={`shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br ${rankBg} font-black text-lg flex items-center justify-center`}>
          #{rank}
        </div>
        {/* Avatar */}
        {c.picture ? (
          <img src={c.picture} alt={c.name} className="w-12 h-12 rounded-full object-cover border-2 border-[#f6c34a]/30 shrink-0" />
        ) : (
          <div className="shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#E50914] to-[#7a050a] border-2 border-[#f6c34a]/30 flex items-center justify-center text-sm font-black">
            {initials}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-bold truncate">{c.name}</div>
          <RoleBadge creator={c} />
        </div>
      </div>
      {c.bio && (
        <p className="text-sm text-gray-400 line-clamp-2 leading-relaxed mb-4">{c.bio}</p>
      )}
      <div className="flex items-end justify-between pt-3 border-t border-white/5">
        <div>
          <div className="text-2xl font-black tabular-nums" style={{ fontFamily: "'Outfit', sans-serif" }}>{headlineStat.value}</div>
          <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">{headlineStat.label}</div>
        </div>
        <div className="flex gap-3 text-xs text-gray-400">
          {sortKey !== "followers" && (
            <div className="text-right">
              <div className="font-bold tabular-nums text-white">{fmt(c.followers)}</div>
              <div className="text-[9px] uppercase tracking-widest text-gray-500">Fol</div>
            </div>
          )}
          {sortKey !== "tickets" && c.ticket_revenue_7d > 0 && (
            <div className="text-right">
              <div className="font-bold tabular-nums text-[#f6c34a]">{money(c.ticket_revenue_7d)}</div>
              <div className="text-[9px] uppercase tracking-widest text-gray-500">7d</div>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function CreatorsLeaderboard() {
  const [sortKey, setSortKey] = useState("followers");
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/creators/top?sort=${sortKey}&limit=60`)
      .then((r) => !cancelled && setCreators(r.data.creators || []))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [sortKey]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <main className="pt-24 px-6 lg:px-12 max-w-[1600px] mx-auto pb-20" data-testid="leaderboard-page">
        {/* Hero header */}
        <section className="relative mb-10 py-8">
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-[0.06] pointer-events-none rounded-2xl overflow-hidden"
            style={{
              backgroundImage:
                "radial-gradient(circle at 15% 30%, #E50914 0%, transparent 45%), radial-gradient(circle at 85% 70%, #f6c34a 0%, transparent 50%)",
            }}
          />
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.3em] text-[#f6c34a] mb-3 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> Creator Leaderboard
            </div>
            <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4" data-testid="leaderboard-heading">
              The creators of FK Cinema
            </h1>
            <p className="text-gray-400 max-w-2xl leading-relaxed">
              The filmmakers, channel owners, and content creators shaping the platform — ranked by what matters most.
            </p>
          </div>
        </section>

        {/* Sort tabs */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="leaderboard-sort-tabs">
          {SORTS.map(({ key, label, Icon }) => (
            <button
              key={key}
              data-testid={`leaderboard-sort-${key}`}
              onClick={() => setSortKey(key)}
              className={`px-4 py-2.5 rounded font-semibold text-sm transition flex items-center gap-2 border ${
                sortKey === key
                  ? "bg-[#f6c34a] text-black border-[#f6c34a]"
                  : "bg-[#0f0f0f] text-gray-300 border-white/10 hover:border-white/30 hover:text-white"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="skeleton rounded-xl h-44" />
            ))}
          </div>
        ) : creators.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-white/10 rounded-xl bg-[#0a0a0a]">
            <Trophy className="w-10 h-10 mx-auto text-[#f6c34a]/40 mb-4" />
            <h3 className="text-xl font-bold mb-2">No creators ranked yet</h3>
            <p className="text-gray-500 text-sm">As soon as approved creators publish content and gain followers, they'll appear here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="leaderboard-grid">
            {creators.map((c, idx) => (
              <CreatorCard key={c.user_id} c={c} rank={idx + 1} sortKey={sortKey} />
            ))}
          </div>
        )}

        <div className="mt-12">
          <Link to="/browse" className="text-sm text-gray-500 hover:text-white inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to Browse
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
